import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ReactComponent as ArrowLeftIcon } from '../icons/arrow.left.svg';
import { ReactComponent as XmarkCircleIcon } from '../icons/xmark.circle.svg';
import { validateEmail } from '../utils/validateEmail';
import { requestVerificationCode } from '../data/repositories/RequestVerificationCode';

export default function RequestVerificationCodePage() {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = new URLSearchParams(location.search);
  const isPromotionAgreed = currentQueryParams.get('isPromotionAgreed') === 'true';
  const inputPlaceholder = t('requestVerificationCodePage.emailPlaceholder');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value);

  const onBackClick = () => {
    if (location.key !== 'default') {
      navigate('/onboarding', { replace: true });
    } else {
      return;
    }
  };

  const clearInput = () => {
    setInput('');
  };

  const isError =
    input &&
    (!validateEmail(input) ||
      input.endsWith('.con') ||
      input.endsWith('gamil.com') ||
      input.replace(/\s/g, '') !== input);

  const postEmail = async () => {
    if (isError) return alert(t('requestVerificationCodePage.emailErrorMessage'));
    setIsLoading(true);
    const response = await requestVerificationCode(input, isPromotionAgreed);
    setIsLoading(false);
    if (response?.success) {
      navigate('/login-with-code?init=true', {
        state: {
          email: response.email
        }
      });
    } else {
      setErrorMessage(response.errorMessage);
    }
  };

  return (
    <div className="flex flex-col bg-background-light dark:bg-background-dark h-screen items-center w-[100vw] max-w-[570px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <button
        className="mt-[14px] px-[16px] mb-[15px] w-full z-50 justify-start"
        onClick={onBackClick}
        type="button"
      >
        <ArrowLeftIcon className="fill-black dark:fill-white" />
      </button>
      <div className="flex flex-col items-center h-screen w-[100vw] max-w-[570px] px-[24px]">
        <div className="flex flex-col mt-[12px] w-full">
          <span className="whitespace-pre-line leading-[30px] text-left text-[26px] font-bold line-height-[120%]">
            {t('requestVerificationCodePage.title')}
          </span>
          <span className="whitespace-pre-line text-main-light-4 dark:text-main-dark-4 mt-[14px] text-[15px]">
            {t('requestVerificationCodePage.description')}
          </span>
        </div>
        <div className="relative w-full mt-[28px]">
          <input
            type="email"
            value={input}
            onChange={handleInputChange}
            placeholder={inputPlaceholder}
            className={`
                w-full
                bg-secondary-light dark:bg-secondary-dark
                hover:bg-secondary-light dark:hover:bg-secondary-dark
                rounded-[12px]
                py-[14px] px-4
                text-gray-900 dark:text-gray-100
                placeholder-gray-400 dark:placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark
                border border-transparent
                `}
          />
          {input && (
            <button
              type="button"
              onClick={clearInput}
              className="
                  absolute right-[16px] top-1/2 
                  -translate-y-1/2 
                  w-[24px] h-[24px]
                  flex items-center justify-center
                "
            >
              <XmarkCircleIcon fill="gray" width={24} height={24} />
            </button>
          )}
        </div>
        {!isError ? (
          input === '' ? (
            <></>
          ) : (
            <></>
          )
        ) : errorMessage !== null && errorMessage ? (
          <div className="flex items-start w-full mt-[4px]">
            <span className="text-[13px] text-red-500">{errorMessage}</span>
          </div>
        ) : (
          <div className="flex items-start w-full mt-[4px]">
            <span className="text-[13px] text-red-500">
              {t('requestVerificationCodePage.emailErrorMessage')}
            </span>
          </div>
        )}
        <div className="mt-[140px] w-full max-w-[570px] px-[24px] absolute bottom-[5%]">
          <button
            onClick={postEmail}
            disabled={input === ''}
            className={`
                  flex items-center justify-center
                  font-bold
                  py-4
                  ${
                    input !== ''
                      ? 'bg-primary-light text-white dark:bg-primary-dark'
                      : 'bg-disabled-light text-onDisabled-light dark:bg-onDisabled-dark cursor-not-allowed'
                  }
                  rounded-[56px]
                  w-full
                  min-h-[52px]
                  disabled:opacity-50
                  `}
            type="button"
          >
            {isLoading ? (
              <div
                className="
                  animate-spin
                  w-5 h-5
                  border-[4px]
                  border-label-light dark:border-label-dark
                  border-t-primary-light dark:border-t-primary-dark
                  rounded-full
                "
              />
            ) : (
              <>{t('next')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
